/**
 * File: notificationsUtils.ts
 * Path: apps/admin/src/shared/utils/notificationsUtils.ts
 * Description: Small pure helpers for the notification pages: how long ago,
 * and whether a device needs attention.
 */

import { DEVICE_CONFIRMED_WITHIN_MS, type PushDevice } from "@idk/config";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now", "5 min ago", "3 h ago", "2 days ago". */
export const timeAgo = (then: number, now: number): string => {
  const gap = Math.max(0, now - then);
  if (gap < MINUTE) return "just now";
  if (gap < HOUR) return `${Math.floor(gap / MINUTE)} min ago`;
  if (gap < DAY) return `${Math.floor(gap / HOUR)} h ago`;
  const days = Math.floor(gap / DAY);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
};

export type DeviceHealth =
  /** Confirmed recently, or nothing sent yet to confirm. */
  | "ok"
  /** Never confirmed anything, though notifications were sent to it. */
  | "never"
  /** Was confirming, then stopped for longer than DEVICE_CONFIRMED_WITHIN_MS while still being sent to. */
  | "stopped";

/**
 * A device that keeps being sent notifications without confirming any is
 * the failure Liam worried about (iPhones drop subscriptions quietly), so
 * Settings flags it. One sent in the last hour gets time to confirm.
 */
export const deviceHealth = (device: Pick<PushDevice, "lastSentAt" | "lastConfirmedAt">, now: number): DeviceHealth => {
  const { lastSentAt, lastConfirmedAt } = device;
  if (lastSentAt === null || now - lastSentAt < HOUR) return "ok";
  if (lastConfirmedAt === null) return "never";
  if (lastConfirmedAt >= lastSentAt) return "ok";
  return now - lastConfirmedAt > DEVICE_CONFIRMED_WITHIN_MS ? "stopped" : "ok";
};

/**
 * A device confirms as soon as its browser hands the notification to the
 * operating system; whether it then pops up is the system's call (a Mac
 * blocks Chrome until allowed, Focus hides everything). The browser can't
 * see that, so after a test each device gets the place to check.
 */
export const systemNotificationTip = (label: string): string => {
  const [device = "", via = ""] = label.split(", ");
  // As the system lists them in its notification settings.
  const SYSTEM_NAMES: Record<string, string> = { Chrome: "Google Chrome", Edge: "Microsoft Edge" };
  const app = via === "app" || via === "" ? "IDK Admin" : (SYSTEM_NAMES[via] ?? via);
  if (device === "Mac") {
    return `Didn't see it? On the Mac: System Settings > Notifications > ${app}: turn on Allow notifications and choose Banners or Alerts. Also check Focus (the moon in Control Centre) is off.`;
  }
  if (device === "Windows PC") {
    return `Didn't see it? On Windows: Settings > System > Notifications: turn on notifications for ${app}, and turn off Do not disturb.`;
  }
  if (device === "iPhone" || device === "iPad") {
    return `Didn't see it? ${device} Settings > Notifications > IDK Admin: turn on Allow Notifications. Also check Focus is off.`;
  }
  if (device.startsWith("Android")) {
    return `Didn't see it? Android Settings > Apps > ${app} > Notifications: allow them, and turn off Do not disturb.`;
  }
  return "Didn't see it? Check this device's notification settings allow the browser, and that Do not disturb or Focus is off.";
};
